import { useId, useState } from "react";

import { Markdown } from "@/components/markdown/Markdown.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import {
  MAX_CONTEXT_CHARS,
  noteButtonLabel,
  withoutImages,
  withoutLinks,
} from "@/shared/userTaskRow.ts";
import type { UserTaskContext } from "@/types/userTasks.ts";

/**
 * Where the editor is drawn, which is what decides how much of itself it
 * shows. One prop rather than a flag beside a surface: they are not
 * independent choices, and a flag let them be set to combinations that mean
 * nothing. Native carries a third, `card`, which this has no equivalent of.
 */
export type ContextSurface = "page" | "row";

/**
 * The context as the card reads it, when there is any.
 *
 * Markdown, which okthx and the user both write; plain text shows as it is.
 * It is written from other people's mail too, so its links only navigate
 * (`untrusted`) and no image loads from it in any form.
 */
export const ContextText = ({
  context,
  links = true,
}: {
  context: UserTaskContext;
  /** Off on a list card, where the card is the one control: see `withoutLinks`. */
  links?: boolean;
}) => (
  <div className="text-xs text-muted-foreground">
    <Markdown
      className="prose prose-zinc max-w-none text-xs text-muted-foreground prose-p:my-0 prose-p:text-muted-foreground"
      untrusted
      // A bare URL is a link too; unwrapped, its words stay.
      disallowedElements={links ? ["img"] : ["img", "a"]}
      unwrapDisallowed
    >
      {links ? withoutImages(context.text) : withoutLinks(withoutImages(context.text))}
    </Markdown>
    {context.by === "user" && <span> (your words)</span>}
  </div>
);

/**
 * The draft and its two buttons.
 *
 * Its own component so it mounts on open: the draft and any failed save start
 * from the saved context every time, rather than from whatever was abandoned
 * last time the editor was closed.
 */
const ContextForm = ({
  context,
  saving,
  onSave,
  statsigName,
  onClose,
}: {
  context: UserTaskContext | null;
  saving: boolean;
  onSave: (text: string) => Promise<unknown>;
  statsigName: string;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState(context?.text ?? "");
  // Generated rather than built from `statsigName`, which repeats whenever two
  // editors are on a page and would give both textareas the same id.
  const textId = useId();
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!draft.trim()) return;
        // Rejection is handled by the mutation's own toast; this only keeps the
        // editor open with the draft intact, and stops an unhandled rejection.
        onSave(draft.trim()).then(onClose, () => {});
      }}
    >
      <Textarea
        id={textId}
        data-statsig-name={`${statsigName}-text`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={3}
        maxLength={MAX_CONTEXT_CHARS}
        className="text-xs"
        aria-label="Where it stands"
      />
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={saving || !draft.trim()}
          data-statsig-name={`${statsigName}-save`}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          data-statsig-name={`${statsigName}-cancel`}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

/**
 * Where a task or a label stands, and the user's way to correct it. okthx keeps
 * the user's correction when it writes again, so the edit is worth making. The
 * editor closes once the save goes through; when it does not, the draft stays
 * and the error says why.
 */
export const ContextEditor = ({
  context,
  saving,
  onSave,
  statsigName,
  open,
  onOpenChange,
  surface = "page",
  subject,
  links,
}: {
  context: UserTaskContext | null;
  saving: boolean;
  /** Resolves when the save went through; rejects when it did not. */
  onSave: (text: string) => Promise<unknown>;
  /** Prefix for the controls' Statsig names: "tasks-task-context", "tasks-label-context". */
  statsigName: string;
  /**
   * Opened from outside, as a task row does from the pencil in its action bar.
   * The line then carries no button of its own, and says nothing at all when
   * there is no context — the pencil is what offers to add it.
   *
   * Left out, the editor owns the toggle and shows its own Edit/Add, which is
   * what a page heading wants: there is no action bar to put a pencil in.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * On a **row** that is not the one open it is the button and nothing else,
   * so every row can offer a note without growing to hold the note it does
   * not have. A **page** shows the note itself.
   */
  surface?: ContextSurface;
  /** What the note is on, for the button's accessible name: a task's title. */
  subject?: string;
  /** See {@link ContextText}. */
  links?: boolean;
}) => {
  const [ownOpen, setOwnOpen] = useState(false);
  const driven = open !== undefined;
  const editing = driven ? open : ownOpen;
  const close = () => (driven ? onOpenChange?.(false) : setOwnOpen(false));

  if (editing) {
    return (
      <ContextForm
        context={context}
        saving={saving}
        onSave={onSave}
        statsigName={statsigName}
        onClose={close}
      />
    );
  }
  if (surface === "row") {
    return (
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs"
          data-statsig-name={`${statsigName}-note`}
          aria-label={subject ? `${noteButtonLabel(context)}: ${subject}` : undefined}
          onClick={() => setOwnOpen(true)}
        >
          {noteButtonLabel(context)}
        </Button>
      </div>
    );
  }
  if (driven) {
    return context ? (
      <div data-context-by={context.by}>
        <ContextText context={context} links={links} />
      </div>
    ) : null;
  }
  return (
    <div className="flex flex-wrap items-baseline gap-2" data-context-by={context?.by}>
      {context ? (
        <ContextText context={context} />
      ) : (
        <p className="text-xs text-muted-foreground">No context yet.</p>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 text-xs"
        data-statsig-name={`${statsigName}-edit`}
        aria-label={context ? "Edit where it stands" : "Add where it stands"}
        onClick={() => setOwnOpen(true)}
      >
        {context ? "Edit" : "Add"}
      </Button>
    </div>
  );
};
